// Computeranalyse einer Partie (wie „Computer analysis“ auf lichess):
// Fehlerzeichen in der Zugliste, Bewertungskurve, Zusammenfassung je Spieler.
import { h, type VNode } from 'snabbdom';
import { Chess } from 'chessops/chess';
import { parseFen } from 'chessops/fen';
import { makeSan } from 'chessops/san';
import { parseUci } from 'chessops/util';
import type { ReviewResult, SideSummary } from '../api';
import { winningChances } from '../ceval';
import type { Node } from '../tree';
import { locale, t } from '../i18n';
import { isPublic } from '../util';

export const GLYPH = { inaccuracy: '?!', mistake: '?', blunder: '??' } as const;
const WORD = { inaccuracy: 'Ungenauigkeit', mistake: 'Fehler', blunder: 'Grober Fehler' } as const; // übersetzt mit t()

/** Index eines Partiezugs (0 = Ausgangsstellung), oder null für eigene Varianten */
export function mainIndex(n: Node): number | null {
  if (!n.game) return null;
  let i = 0;
  for (let p = n.parent; p; p = p.parent) i++;
  return i;
}

export function judgementOf(r: ReviewResult | null, n: Node) {
  if (!r || r.status !== 'done') return undefined;
  const i = mainIndex(n);
  return i === null ? undefined : r.judgements.find(j => j.ply === i);
}

/** „Fehler. Besser war Sf3.“ für den aktuellen Zug */
export function comment(r: ReviewResult | null, n: Node): VNode | null {
  const j = judgementOf(r, n);
  if (!j || !n.parent) return null;
  let best = '';
  if (j.best) {
    const pos = Chess.fromSetup(parseFen(n.parent.fen).unwrap()).unwrap();
    const m = parseUci(j.best);
    if (m) best = makeSan(pos, m);
  }
  return h(`p.review-comment.${j.judgement}`, [
    h('strong', `${t(WORD[j.judgement])}.`),
    best ? ' ' + t('Besser war {m}.', { m: best }) : '',
  ]);
}

function sideRow(label: string, s: SideSummary | null): VNode {
  const cell = (v: number | null | undefined, cls = '') => h(`td${cls}`, v === null || v === undefined ? '—' : String(v));
  return h('tr', [h('th', label), cell(s?.inaccuracies, '.inaccuracy'), cell(s?.mistakes, '.mistake'), cell(s?.blunders, '.blunder'), cell(s?.acpl), cell(s?.accuracy)]);
}

/** Bewertungskurve: Gewinnchance für Weiß je Halbzug, Klick springt zum Zug */
function graph(r: ReviewResult, current: number | null, jump: (i: number) => void, whiteStarts: boolean): VNode {
  const W = 600;
  const H = 110;
  const n = r.evals.length;
  const x = (i: number) => (n <= 1 ? 0 : (i / (n - 1)) * W);
  const y = (i: number) => {
    const e = r.evals[i];
    if (!e) return H / 2;
    // Matt 0: die Seite am Zug ist matt
    const whiteToMove = (i % 2 === 0) === whiteStarts;
    const wc = e.mate === 0 ? (whiteToMove ? -1 : 1) : winningChances({ cp: e.cp, mate: e.mate });
    return H / 2 - (wc * H) / 2;
  };
  const pts = r.evals.map((_, i) => `${x(i).toFixed(1)},${y(i).toFixed(1)}`);
  const area = `M0,${H} L${pts.join(' L')} L${W},${H} Z`;
  const line = `M${pts.join(' L')}`;
  const dots = r.judgements.map(j =>
    h('circle', { attrs: { cx: x(j.ply), cy: y(j.ply), r: 3.5, class: j.judgement } }),
  );
  return h('div.review-graph', [
    h('svg', {
      attrs: { viewBox: `0 0 ${W} ${H}`, preserveAspectRatio: 'none', role: 'img', 'aria-label': t('Bewertungsverlauf') },
      on: {
        click: (e: MouseEvent) => {
          const rect = (e.currentTarget as SVGElement).getBoundingClientRect();
          jump(Math.round(((e.clientX - rect.left) / rect.width) * (n - 1)));
        },
      },
    }, [
      h('rect', { attrs: { x: 0, y: 0, width: W, height: H, class: 'bg' } }),
      h('path', { attrs: { d: area, class: 'area' } }),
      h('line', { attrs: { x1: 0, x2: W, y1: H / 2, y2: H / 2, class: 'mid' } }),
      h('path', { attrs: { d: line, class: 'line', 'vector-effect': 'non-scaling-stroke' } }),
      current !== null ? h('line', { attrs: { x1: x(current), x2: x(current), y1: 0, y2: H, class: 'cur', 'vector-effect': 'non-scaling-stroke' } }) : null,
      ...dots,
    ]),
  ]);
}

export function reviewBox(
  r: ReviewResult | null,
  node: Node,
  names: { white: string; black: string },
  actions: { start: () => void; jump: (i: number) => void },
  whiteStarts: boolean,
): VNode | null {
  if (!r) return null;
  if (r.status === 'none' || r.status === 'error') {
    if (isPublic) return null; // Demo: Auswertungen nur, wenn schon vorhanden
    return h('div.review', [
      r.status === 'error' ? h('p.error', t('Auswertung abgebrochen: {e}', { e: r.error ?? t('unbekannter Fehler') })) : null,
      r.available === false
        ? h('p.muted', t('Für die Computeranalyse wird Stockfish gebraucht.'))
        : h('button.btn.accent', { on: { click: actions.start } }, r.status === 'error' ? t('Computeranalyse neu starten') : t('Computeranalyse starten')),
    ]);
  }
  if (r.status === 'running') {
    const pct = r.total ? Math.round((r.done / r.total) * 100) : 0;
    return h('div.review', [
      h('div.progress', [h('div.bar', { style: { width: `${pct}%` } })]),
      h('p.muted.small', t('Stockfish bewertet Stellung {a} von {b} ({n} Mio. Knoten je Stellung) …', { a: r.done, b: r.total, n: (r.nodes / 1e6).toLocaleString(locale()) })),
    ]);
  }
  return h('div.review', [
    graph(r, mainIndex(node), actions.jump, whiteStarts),
    // Erklärung der Spalten als Tooltip statt als Absatz: die Höhe bekommt die Zugliste
    h('table.review-sum', { attrs: { title: t('Ungenauigkeit ?!, Fehler ?, grober Fehler ??; Ø Verlust in Hundertstel-Bauern; Genauigkeit in %. Berechnet wie auf lichess.') } }, [
      h('thead', h('tr', [h('th', ''), h('th.inaccuracy', '?!'), h('th.mistake', '?'), h('th.blunder', '??'), h('th', t('Ø Verlust')), h('th', t('Genauigkeit'))])),
      h('tbody', [sideRow(names.white, r.white), sideRow(names.black, r.black)]),
    ]),
  ]);
}
